import React from 'react';
import { Box, alpha } from '@mui/material';
import {
  Movie as MovieIcon, Image as ImageIcon, TextFields as TextIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';
import { getToken } from '../api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Toutes les images d'un post de la library (ids -> URLs).
function imageIds(post) {
  const ids = post?.imageUploadIds || post?.uploadIds || [];
  return Array.isArray(ids) ? ids.filter(Boolean) : [];
}
function videoId(post) {
  return post?.videoData?.uploadId || null;
}
const rawUrl = (id) => `${API}/api/uploads/${id}/raw?token=${getToken()}`;

// Affiche directement les miniatures d'un post (1, 2, 3… images) côte à côte,
// avec un badge +N si le post en contient plus que `max`. Évite d'avoir à
// cliquer pour découvrir combien d'images il y a.
export default function ThumbStack({ post, size = 34, max = 3, gap = 3 }) {
  const imgs = imageIds(post);
  const vid = videoId(post);

  const cell = (children, key, extraSx = {}) => (
    <Box key={key} sx={{
      width: size, height: size, borderRadius: 1.25, flexShrink: 0, overflow: 'hidden',
      position: 'relative', border: `1px solid ${t.border}`,
      bgcolor: alpha('#fff', 0.05), display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: t.mutedFaint, ...extraSx,
    }}>{children}</Box>
  );

  // Vidéo : une vignette avec un badge lecture.
  if (vid && imgs.length === 0) {
    return cell(
      <>
        <Box component="img" src={rawUrl(vid)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#000', 0.25) }}>
          <MovieIcon sx={{ fontSize: size * 0.5, color: '#fff' }} />
        </Box>
      </>, 'vid');
  }

  // Pas d'image : simple icône texte.
  if (imgs.length === 0) {
    return cell(<TextIcon sx={{ fontSize: size * 0.5 }} />, 'txt');
  }

  const shown = imgs.slice(0, max);
  const extra = imgs.length - shown.length;

  return (
    <Box sx={{ display: 'flex', gap: `${gap}px`, flexShrink: 0 }}>
      {shown.map((id, i) => {
        const isLast = i === shown.length - 1;
        return cell(
          <>
            <Box component="img" src={rawUrl(id)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            {isLast && extra > 0 && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha('#000', 0.55), color: '#fff', fontSize: size * 0.34, fontWeight: 700 }}>
                +{extra}
              </Box>
            )}
          </>, id, { bgcolor: alpha(t.primary, 0.12) });
      })}
    </Box>
  );
}

// Icône de repli (type de média) si jamais on veut juste l'indicateur.
export function mediaIconOf(post, sx = {}) {
  if (videoId(post)) return <MovieIcon sx={sx} />;
  if (imageIds(post).length) return <ImageIcon sx={sx} />;
  return <TextIcon sx={sx} />;
}
